import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
// @ts-ignore
import Cookies from 'js-cookie';

import { apiClient } from '../../api/httpClient';
import { AuthHeader, defaultAuthMenuItems } from './AuthHeader';
import { constants } from '../../config/constants';
import { setUser } from '../../store/auth';
import { getUserOrganizations } from '../../store/user_organizations';
import { getServices } from '../../store/global/services';
import { zynity_api } from '../../config/external-routes';
import { getAccessTokenCookieOptions } from '../../helpers/cookieOptions';

const toTitleCase = (value: string | null) => {
  if (!value) return 'Social';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const parseHashParams = (hash: string) => {
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  return new URLSearchParams(trimmed);
};

const formatError = (raw?: string | null) => {
  const normalized = raw || '';
  if (normalized.toLowerCase().includes('no matching user found')) {
    return 'Looks as that you are here for first time, please Sign Up here.';
  }
  return normalized || 'Unable to complete sign in. Please try again.';
};

const secondsToDays = (seconds?: string | null) => {
  const value = seconds ? parseInt(seconds, 10) : NaN;
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return value / 86400;
};

export const SocialAuthCompletePage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hashParams = useMemo(() => parseHashParams(location.hash), [location.hash]);

  const status = hashParams.get('status') || searchParams.get('status');
  const provider = hashParams.get('provider') || searchParams.get('provider');
  const message = hashParams.get('message') || searchParams.get('message');
  const providerLabel = toTitleCase(provider);
  const redirectStorageKey = constants.post_login_redirect_storage_key;

  const getRedirectPath = () => {
    try {
      const storedPath = localStorage.getItem(redirectStorageKey);
      if (!storedPath) return null;
      if (!storedPath.startsWith('/')) return null;
      if (storedPath === '/' || storedPath === '/login' || storedPath === '/site/login') return null;
      return storedPath;
    } catch {
      return null;
    }
  };

  const clearRedirectPath = () => {
    try {
      localStorage.removeItem(redirectStorageKey);
    } catch {
      // ignore storage errors
    }
  };

  useEffect(() => {
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const expiresIn = hashParams.get('expires_in');
    const refreshExpiresIn = hashParams.get('refresh_expires_in');

    if (!accessToken) {
      setIsLoading(false);
      const fallbackStatus = status || 'error';
      if (fallbackStatus === 'success') {
        setError(formatError('Unable to complete sign in. Please try again.'));
      } else {
        setError(formatError(message));
      }
      return;
    }

    const hydrateUser = async () => {
      try {
        const expiresDays = secondsToDays(expiresIn);
        const cookieOptions = getAccessTokenCookieOptions(
          expiresDays ? { expires: expiresDays } : undefined
        );
        Cookies.set(constants.zynity_api_access_token_cookie_name, accessToken, cookieOptions);

        axios.defaults.headers.common = { Authorization: `Bearer ${accessToken}` };
        apiClient.defaults.headers.common = {
          ...(apiClient.defaults.headers.common || {}),
          Authorization: `Bearer ${accessToken}`,
        };

        const response = await apiClient.get(zynity_api.retrieve_authenticated_user);
        const userData = response.data?.user || response.data;

        if (!userData) {
          throw new Error('Unable to load your account. Please try again.');
        }

        const hydratedUser = {
          ...userData,
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: expiresIn,
          refresh_expires_in: refreshExpiresIn,
        };

        dispatch(setUser(hydratedUser));
        dispatch(getUserOrganizations());
        dispatch(getServices());

        const redirectPath = getRedirectPath();
        clearRedirectPath();
        navigate(redirectPath || '/', { replace: true });
        // Fallback in case react-router navigation is blocked for any reason.
        setTimeout(() => {
          window.location.replace(redirectPath || '/');
        }, 300);
      } catch (err: any) {
        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to complete sign in. Please try again.';
        setError(formatError(apiMessage));
      } finally {
        setIsLoading(false);
      }
    };

    hydrateUser();
  }, [dispatch, hashParams, message, navigate, searchParams, status]);

  const handleRetry = () => {
    clearRedirectPath();
    navigate('/site/login', { replace: true });
  };

  const statusText =
    status === 'success'
      ? `Completing ${providerLabel} sign in...`
      : 'Finishing sign in...';

  return (
    <div className="login-page">
      <AuthHeader menuItems={defaultAuthMenuItems} />

      <main className="login-content">
        <h1 className="login-title">Finishing Login</h1>
        <section className="login-card" aria-live="polite">
          {error ? (
            <>
              <p className="login-error" role="alert">
                {error}
              </p>
              <div className="social-complete-actions">
                {formatError(error).toLowerCase().includes('sign up here') && (
                  <a className="sign-in-btn" href="/people/register">
                    Sign Up
                  </a>
                )}
                <button type="button" className="sign-in-btn" onClick={handleRetry}>
                  Back to Login
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="login-helper-text">{statusText}</p>
              {isLoading && <p className="login-helper-text subtle">This should only take a moment.</p>}
            </>
          )}
        </section>
      </main>
    </div>
  );
};
